import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { SimResult } from "../enterprise/admin/RoutingRules";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ujmsIntakeConfig, ujmsPersons, ujmsClerkIntake } from "../../services/ujmsApi";
import { Panel, Field, inputCls, selectCls, btnPrimary, btnGhost, btnDanger, thCls, tdCls, Badge, fmtD } from "../ui";
import { Search, UserPlus, Building2, Trash2, ChevronRight, ChevronLeft, CheckCircle2 } from "lucide-react";
import { uploadFileChunked } from "../../services/upload";
import { CivilTrackPicker, FeeQuote } from "../enterprise/civil/CivilTrackPicker";
import { PartyDetailsFields, ExtraDocuments, emptyDetails, cleanDetails, counselLabel, addressLabel } from "../enterprise/civil/IntakeAdvanced";

const STEPS = ["Classification", "Parties", "Initiating Filing", "Review & Create"];

export default function IntakeWizard() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    jurisdiction_id: "", division_id: "", district_id: "", case_type: "CRIMINAL",
    case_subtype: "", filing_date: new Date().toISOString().slice(0, 10),
    caption: "", confidentiality_level: "PUBLIC", parties: [], initiating_filing: null,
    civil_track: "", relief: {}, documents: [],
  });

  useEffect(() => {
    ujmsIntakeConfig().then((r) => {
      setCfg(r.data);
      setForm((f) => ({ ...f, jurisdiction_id: r.data.jurisdictions[0]?.id || "" }));
    }).catch(() => toast.error("Could not load intake configuration"));
  }, []);

  const caseType = cfg?.case_types?.find((c) => c.code === form.case_type);
  const isCivilCategory = (caseType?.category || "Civil") !== "Criminal";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const defendants = form.parties.filter((p) => (p.relationship_type || "").toUpperCase() === "DEFENDANT");
  const missingRelief = form._track ? form._track.relief.filter((r) => r.required && r.type !== "bool" && !(form.relief?.[r.key] ?? "").toString().trim()) : [];

  const canNext = () => {
    if (step === 0) return form.jurisdiction_id && (form.division_id || form.auto_route) && form.case_type && !missingRelief.length;
    if (step === 1) return form.case_type === "CRIMINAL" ? defendants.length > 0 : form.parties.length > 0;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form, caption: form.caption || undefined, district_id: form.district_id || undefined,
        amount_in_controversy: form.amount_in_controversy === "" || form.amount_in_controversy == null ? undefined : Number(form.amount_in_controversy),
        municipality: form.municipality || undefined, fee_waiver: form.fee_waiver_requested ? { basis: form.fee_waiver_basis || "INCOME_AT_OR_BELOW_125_PCT", statement: form.fee_waiver_statement || "" } : undefined,
        civil_track: form.civil_track || undefined, relief: form.civil_track ? form.relief : undefined,
        documents: (form.documents || []).map(({ _size, ...d }) => ({ ...d, filing_type: d.filing_type || undefined })),
        parties: form.parties.map(({ _display, ...p }) => p) };
      delete payload.fee_waiver_requested; delete payload.fee_waiver_basis; delete payload.fee_waiver_statement; delete payload._routing; delete payload._track;
      const r = await ujmsClerkIntake(payload);
      const fee = r.data.filing_fee?.amount != null ? ` · fee $${Number(r.data.filing_fee.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "";
      toast.success(`CASE_CREATED — ${r.data.case.case_number}${r.data.civil ? " · " + r.data.civil.track_label : ""}${r.data.routing?.matched ? " · routed by " + r.data.routing.rule.name : ""}${r.data.fee_waiver ? " · fees suspended (Rule 298)" : fee}`);
      navigate(`/cases/${r.data.case.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Intake failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl space-y-5" data-testid="intake-wizard">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">New Case Intake</h1>
        <p className="text-sm text-slate-500">Receive a filing, resolve parties against the person master, open the official case.</p>
      </div>

      <ol className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide" data-testid="intake-steps">
        {STEPS.map((s, i) => (
          <li key={s} className={`flex items-center gap-1 px-3 py-1.5 border rounded-sm ${i === step ? "bg-blue-900 text-white border-blue-900" : i < step ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-white text-slate-400 border-slate-200"}`}>
            {i < step && <CheckCircle2 className="w-3.5 h-3.5" />} {i + 1}. {s}
          </li>
        ))}
      </ol>

      {step === 0 && cfg && (
        <Panel title="Step 1 — Case Classification" testId="intake-step-classification">
          <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Jurisdiction" required>
              <select className={selectCls} value={form.jurisdiction_id} onChange={(e) => set("jurisdiction_id", e.target.value)} data-testid="intake-jurisdiction">
                {cfg.jurisdictions.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
              </select>
            </Field>
            <Field label="Division" required>
              <select className={selectCls} value={form.division_id} onChange={(e) => set("division_id", e.target.value)} data-testid="intake-division">
                <option value="">Select division…</option>
                {cfg.divisions.map((d) => <option key={d.id} value={d.id} disabled={["Criminal Division", "Felony Division"].includes(d.name)}>{["Criminal Division", "Felony Division"].includes(d.name) ? `${d.name} — reviewed assignment required` : d.name}</option>)}
              </select>
            </Field>
            <Field label="District">
              <select className={selectCls} value={form.district_id} onChange={(e) => set("district_id", e.target.value)} data-testid="intake-district">
                <option value="">—</option>
                {cfg.districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </Field>
            <Field label="Case Type" required>
              <select className={selectCls} value={form.case_type} onChange={(e) => set("case_type", e.target.value, set("case_subtype", ""))} data-testid="intake-case-type">
                {["Criminal", "Civil", "Family", "Probate & Mental Health"].map((cat) => (
                  <optgroup key={cat} label={cat}>
                    {cfg.case_types.filter((c) => (c.category || "Civil") === cat).map((c) => (
                      <option key={c.code} value={c.code} disabled={!c.enabled}
                        style={c.confidential ? { color: "#b91c1c", fontWeight: 600 } : undefined}>
                        {c.label}{c.confidential ? " — confidential" : ""}{!c.enabled ? " (not enabled)" : ""}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </Field>
            <Field label="Case Number (manual entry — leave blank to auto-issue)">
              <input className={inputCls} placeholder="e.g. 24CVS001234-910 (legacy / manual entry)"
                value={form.case_number || ""} onChange={(e) => set("case_number", e.target.value.toUpperCase())}
                data-testid="intake-manual-number" />
            </Field>
            <Field label="Case Subtype">
              <select className={selectCls} value={form.case_subtype} onChange={(e) => set("case_subtype", e.target.value)} data-testid="intake-case-subtype">
                <option value="">—</option>
                {(caseType?.subtypes || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Filing Date" required>
              <input type="date" className={inputCls} value={form.filing_date} onChange={(e) => set("filing_date", e.target.value)} data-testid="intake-filing-date" />
            </Field>
            <Field label="Security Classification">
              <select className={selectCls} value={form.confidentiality_level} onChange={(e) => set("confidentiality_level", e.target.value)} data-testid="intake-confidentiality">
                {cfg.confidentiality_levels.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            {form.case_type !== "CRIMINAL" && (
              <Field label="Case Caption" required>
                <input className={inputCls} value={form.caption} onChange={(e) => set("caption", e.target.value)} placeholder="e.g. Smith v. Jones" data-testid="intake-caption" />
              </Field>
            )}
          </div>
          <RoutingAssist form={form} set={set} setForm={setForm} cfg={cfg} />
          {isCivilCategory && <CivilTrackPicker form={form} setForm={setForm} />}
          {missingRelief.length > 0 && <p className="px-4 pb-3 text-xs text-red-700" data-testid="intake-relief-missing">Required for {form._track.label}: {missingRelief.map((r) => r.label.split(" (")[0]).join(", ")}</p>}
        </Panel>
      )}

      {step === 1 && cfg && (
        <PartiesStep cfg={cfg} form={form} setForm={setForm} />
      )}

      {step === 2 && cfg && (
        <FilingStep cfg={cfg} form={form} set={set} />
      )}

      {step === 3 && cfg && (
        <Panel title="Step 4 — Review & Create" testId="intake-step-review">
          <div className="p-4 space-y-3 text-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <ReviewItem label="Case Type" value={`${caseType?.label || form.case_type}${form.case_subtype ? " — " + form.case_subtype : ""}`} />
              <ReviewItem label="Division" value={cfg.divisions.find((d) => d.id === form.division_id)?.name} />
              <ReviewItem label="District" value={cfg.districts.find((d) => d.id === form.district_id)?.name || "—"} />
              <ReviewItem label="Filing Date" value={fmtD(form.filing_date)} />
              <ReviewItem label="Routing" value={form._routing?.matched ? `${form._routing.rule.source === "GAO" ? "GAO" : "Rule"}: ${form._routing.rule.name}` : form.auto_route ? "Rules engine decides on create" : "Manual"} />
              <ReviewItem label="Civil track" value={form._track ? `${form._track.label} · ${form._track.statute}` : "General (no track)"} />
              <ReviewItem label="Amount in controversy" value={form.amount_in_controversy ? `$${Number(form.amount_in_controversy).toLocaleString()}` : "—"} />
              <ReviewItem label="Filing fee" value={form.fee_waiver_requested ? "Rule 298 petition — fees SUSPENDED pending ruling" : "Per Clerk fee schedule (auto-calculated below)"} />
              <ReviewItem label="Litigant" value={form.is_srl ? "Self-represented" : "Represented by counsel"} />
            </div>
            {form._track && Object.keys(form.relief || {}).length > 0 && (
              <div data-testid="intake-review-relief">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Relief requested</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-slate-700">
                  {form._track.relief.filter((r) => { const v = form.relief?.[r.key]; return v !== "" && v != null && v !== false; }).map((r) => <div key={r.key}><span className="text-slate-500">{r.label.split(" (")[0]}:</span> {r.type === "bool" ? "Yes" : r.type === "money" ? `$${Number(form.relief[r.key]).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : form.relief[r.key]}</div>)}
                </div>
              </div>
            )}
            {!form.fee_waiver_requested && (form._track || ["CIVIL", "LAW"].includes(form.case_type)) && <FeeQuote track={form.civil_track} amount={form.relief?.amount ?? form.amount_in_controversy} juryDemand={form.jury_demand} caseType={form.case_type} possessionOnly={form.civil_track === "EVICTION" && !(Number(form.relief?.amount || 0) > 0)} testId="intake-review-fee" />}
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Parties ({form.parties.length})</div>
              {form.parties.map((p, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 py-1 border-b border-slate-100 text-slate-700" data-testid={`intake-review-party-${i}`}>
                  <Badge value={p.relationship_type} />
                  <span>{p.org_name || `${p.first_name || ""} ${p.last_name || ""}`.trim() || p._display}</span>
                  <span className="text-xs text-slate-400">{addressLabel(p)}</span>
                  <span className="text-xs text-slate-500 ml-auto">Counsel: {counselLabel(p)}</span>
                </div>
              ))}
            </div>
            <ReviewItem label="Initiating Filing" value={form.initiating_filing ? `${form.initiating_filing.filing_type} — ${form.initiating_filing.filing_title}${form.initiating_filing.document ? " (document attached)" : ""}` : "None registered"} />
            <ReviewItem label={`Additional documents (${form.documents.length})`} value={form.documents.length ? form.documents.map((d) => `${d.title} [${d.document_type}${d.filing_type ? ` → file as ${d.filing_type}` : " → register"}]`).join("; ") : "None"} />
            <p className="text-xs text-slate-400">The case number is generated by the Case Number Service on creation — manual numbers are refused. Every action below is docketed and audited.</p>
          </div>
        </Panel>
      )}

      <div className="flex items-center justify-between">
        <button className={btnGhost} disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="intake-back-btn">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        {step < 3 ? (
          <button className={btnPrimary} disabled={!canNext()} onClick={() => setStep(step + 1)} data-testid="intake-next-btn">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button className={btnPrimary} disabled={busy} onClick={submit} data-testid="intake-create-btn">
            {busy ? "Creating…" : "Create Case"}
          </button>
        )}
      </div>
    </div>
  );
}

const ReviewItem = ({ label, value }) => (
  <div><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-slate-800">{value || "—"}</div></div>
);

function PartiesStep({ cfg, form, setForm }) {
  const [mode, setMode] = useState("search");
  const [sf, setSf] = useState({ first_name: "", last_name: "", dob: "", alias: "" });
  const [results, setResults] = useState(null);
  const [pick, setPick] = useState(null);
  const [rel, setRel] = useState("Defendant");
  const [det, setDet] = useState(emptyDetails());
  const [np, setNp] = useState({ first_name: "", middle_name: "", last_name: "", suffix: "", dob: "", gender: "", preferred_name: "", aliases: "" });
  const [org, setOrg] = useState({ org_name: "", party_type: "Organization" });

  const search = async () => {
    const params = {};
    ["first_name", "last_name", "dob", "alias"].forEach((k) => { if (sf[k]) params[k] = sf[k]; });
    if (!Object.keys(params).length) { toast.error("Enter at least one search field"); return; }
    const r = await ujmsPersons(params);
    setResults(r.data);
    setPick(null);
  };

  const addParty = (party) => {
    const extra = cleanDetails(det);
    if (extra.is_srl) ["attorney_name", "attorney_ardc", "attorney_firm", "attorney_email", "attorney_phone", "attorney_address", "attorney_code"].forEach((k) => delete extra[k]);
    setForm((f) => ({ ...f, parties: [...f.parties, { ...extra, ...party }] }));
    setResults(null); setPick(null); setDet(emptyDetails());
    setNp({ first_name: "", middle_name: "", last_name: "", suffix: "", dob: "", gender: "", preferred_name: "", aliases: "" });
    setOrg({ org_name: "", party_type: "Organization" });
    toast.success("Party added to the case");
  };

  const removeParty = (i) => setForm((f) => ({ ...f, parties: f.parties.filter((_, ix) => ix !== i) }));
  const relOptions = (cfg.relationship_types || []).map((r) => r.name);

  return (
    <div className="space-y-4">
      <Panel title="Parties on this case" testId="intake-parties-list">
        {form.parties.length === 0 ? (
          <p className="p-4 text-sm text-slate-400">No parties yet. {form.case_type === "CRIMINAL" && "A criminal case requires at least one DEFENDANT."}{form._track && ` ${form._track.label}: ${form._track.roles.filter(Boolean).join(" and ")}.`}</p>
        ) : (
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>Relationship</th><th className={thCls}>Party</th><th className={thCls}>Address / contact</th><th className={thCls}>Counsel (ARDC)</th><th className={thCls}></th></tr></thead>
            <tbody>
              {form.parties.map((p, i) => (
                <tr key={i} data-testid={`intake-party-row-${i}`}>
                  <td className={tdCls}><Badge value={p.relationship_type} /></td>
                  <td className={tdCls}>{p.org_name || p._display || `${p.first_name || ""} ${p.last_name || ""}`.trim()}{p.dob && <span className="text-xs text-slate-400 ml-2">DOB {p.dob}</span>}</td>
                  <td className={tdCls}><div>{addressLabel(p)}</div>{(p.phone || p.email) && <div className="text-[11px] text-slate-500">{[p.phone, p.email].filter(Boolean).join(" · ")}</div>}{p.service_address && <div className="text-[11px] text-amber-800">Serve at: {p.service_address}</div>}</td>
                  <td className={tdCls} data-testid={`intake-party-counsel-${i}`}>{counselLabel(p)}</td>
                  <td className={tdCls}><button className={btnDanger} onClick={() => removeParty(i)} data-testid={`intake-remove-party-${i}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel title="Step 2 — Add a party (search the person registry first)" testId="intake-step-parties">
        <div className="px-4 pt-3 flex gap-1">
          {[["search", "Search Person", Search], ["new", "Create New Person", UserPlus], ["org", "Organization / State", Building2]].map(([m, label, Icon]) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`party-mode-${m}`}
              className={`flex items-center gap-1.5 text-sm px-3 py-1.5 border-b-2 ${mode === m ? "border-blue-900 text-blue-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
        <div className="p-4">
          {mode === "search" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Field label="First Name"><input className={inputCls} value={sf.first_name} onChange={(e) => setSf({ ...sf, first_name: e.target.value })} data-testid="party-search-first" /></Field>
                <Field label="Last Name"><input className={inputCls} value={sf.last_name} onChange={(e) => setSf({ ...sf, last_name: e.target.value })} data-testid="party-search-last" /></Field>
                <Field label="Date of Birth"><input type="date" className={inputCls} value={sf.dob} onChange={(e) => setSf({ ...sf, dob: e.target.value })} data-testid="party-search-dob" /></Field>
                <Field label="Known Alias"><input className={inputCls} value={sf.alias} onChange={(e) => setSf({ ...sf, alias: e.target.value })} data-testid="party-search-alias" /></Field>
                <div className="flex items-end"><button className={btnPrimary} onClick={search} data-testid="party-search-btn"><Search className="w-4 h-4" /> Search</button></div>
              </div>
              {results && (
                <table className="w-full border-collapse" data-testid="party-search-results">
                  <thead><tr><th className={thCls}>Name</th><th className={thCls}>DOB</th><th className={thCls}>Existing Cases</th><th className={thCls}></th></tr></thead>
                  <tbody>
                    {results.map((p) => (
                      <tr key={p.id} className={pick?.id === p.id ? "bg-blue-50" : "hover:bg-slate-50"}>
                        <td className={tdCls}>{p.display_name}{p.middle_name && <span className="text-slate-400"> {p.middle_name}</span>}</td>
                        <td className={`${tdCls} font-mono text-xs`}>{p.dob || "—"}</td>
                        <td className={tdCls}>{p.case_count}</td>
                        <td className={tdCls}><button className={btnGhost} onClick={() => setPick(p)} data-testid={`party-select-${p.id}`}>Select Existing</button></td>
                      </tr>
                    ))}
                    {!results.length && <tr><td colSpan={4} className={tdCls}>No matches — you may create a new person.</td></tr>}
                  </tbody>
                </table>
              )}
              {pick && (
                <div className="space-y-3 border border-blue-200 bg-blue-50 rounded-sm p-3">
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="text-sm font-medium text-blue-900">{pick.display_name} {pick.dob ? `(DOB ${pick.dob})` : ""}</div>
                    <Field label="Relationship"><select className={selectCls} value={rel} onChange={(e) => setRel(e.target.value)} data-testid="party-existing-relationship">{relOptions.map((r) => <option key={r}>{r}</option>)}</select></Field>
                    <button className={btnPrimary} data-testid="party-add-existing-btn"
                      onClick={() => addParty({ person_id: pick.id, relationship_type: rel, _display: pick.display_name, dob: pick.dob })}>
                      Add to Case
                    </button>
                  </div>
                  <PartyDetailsFields det={det} setDet={setDet} testPrefix="party-existing" />
                </div>
              )}
            </div>
          )}
          {mode === "new" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Legal First Name" required><input className={inputCls} value={np.first_name} onChange={(e) => setNp({ ...np, first_name: e.target.value })} data-testid="party-new-first" /></Field>
                <Field label="Middle Name"><input className={inputCls} value={np.middle_name} onChange={(e) => setNp({ ...np, middle_name: e.target.value })} /></Field>
                <Field label="Legal Last Name" required><input className={inputCls} value={np.last_name} onChange={(e) => setNp({ ...np, last_name: e.target.value })} data-testid="party-new-last" /></Field>
                <Field label="Suffix"><input className={inputCls} value={np.suffix} onChange={(e) => setNp({ ...np, suffix: e.target.value })} /></Field>
                <Field label="Date of Birth"><input type="date" className={inputCls} value={np.dob} onChange={(e) => setNp({ ...np, dob: e.target.value })} data-testid="party-new-dob" /></Field>
                <Field label="Gender"><input className={inputCls} value={np.gender} onChange={(e) => setNp({ ...np, gender: e.target.value })} /></Field>
                <Field label="Preferred Name"><input className={inputCls} value={np.preferred_name} onChange={(e) => setNp({ ...np, preferred_name: e.target.value })} /></Field>
                <Field label="Relationship" required><select className={selectCls} value={rel} onChange={(e) => setRel(e.target.value)} data-testid="party-new-relationship">{relOptions.map((r) => <option key={r}>{r}</option>)}</select></Field>
              </div>
              <PartyDetailsFields det={det} setDet={setDet} testPrefix="party-new" />
              <button className={btnPrimary} disabled={!np.first_name || !np.last_name} data-testid="party-add-new-btn"
                onClick={() => addParty({ first_name: np.first_name, middle_name: np.middle_name || undefined, last_name: np.last_name, suffix: np.suffix || undefined, dob: np.dob || undefined, relationship_type: rel })}>
                <UserPlus className="w-4 h-4" /> Add New Person to Case
              </button>
              <p className="text-xs text-slate-400">The person master de-duplicates on legal name + DOB — an existing identity will be reused, never copied. Address, phone and email are written to the person record.</p>
            </div>
          )}
          {mode === "org" && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-end gap-3">
                <Field label="Organization / Agency Name" required><input className={`${inputCls} min-w-[280px]`} value={org.org_name} onChange={(e) => setOrg({ ...org, org_name: e.target.value })} data-testid="party-org-name" /></Field>
                <Field label="Kind"><select className={selectCls} value={org.party_type} onChange={(e) => setOrg({ ...org, party_type: e.target.value })}><option>Organization</option><option>Government Agency</option></select></Field>
                <Field label="Relationship"><select className={selectCls} value={rel} onChange={(e) => setRel(e.target.value)} data-testid="party-org-relationship">{relOptions.map((r) => <option key={r}>{r}</option>)}</select></Field>
                <button className={btnPrimary} disabled={!org.org_name} data-testid="party-add-org-btn"
                  onClick={() => addParty({ party_type: org.party_type, org_name: org.org_name, relationship_type: rel })}>
                  <Building2 className="w-4 h-4" /> Add Organization
                </button>
                <button className={btnGhost} data-testid="party-add-state-btn"
                  onClick={() => addParty({ party_type: "Government Agency", org_name: "People of the State of Illinois", relationship_type: "State" })}>
                  + People of the State of Illinois
                </button>
              </div>
              <PartyDetailsFields det={det} setDet={setDet} testPrefix="party-org" />
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}

function FilingStep({ cfg, form, set }) {
  const initiating = (cfg.filing_types || []).filter((f) => f.is_initiating);
  const f = form.initiating_filing || { filing_type: "Complaint", filing_title: "", document: null };
  const upd = (patch) => set("initiating_filing", { ...f, ...patch });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const rec = await uploadFileChunked(file, { purpose: "DOCUMENT" });
      upd({ document: { title: f.filing_title || file.name, filename: file.name, mime_type: rec.content_type || file.type || "application/pdf", upload_id: rec.upload_id } });
      toast.success(`Document uploaded to court storage: ${file.name}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
  };

  return (
    <Panel title="Step 3 — Register the initiating filing" testId="intake-step-filing">
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Filing Type" required>
            <select className={selectCls} value={f.filing_type} onChange={(e) => upd({ filing_type: e.target.value })} data-testid="intake-filing-type">
              {initiating.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
            </select>
          </Field>
          <Field label="Filing Title" required>
            <input className={inputCls} value={f.filing_title} onChange={(e) => upd({ filing_title: e.target.value })}
              placeholder="e.g. Felony Criminal Complaint — 720 ILCS 5/18-1" data-testid="intake-filing-title" />
          </Field>
          <Field label="Document (PDF/image, ≤ 4MB)">
            <input type="file" accept=".pdf,image/*" onChange={onFile} className="text-sm" data-testid="intake-filing-file" />
          </Field>
        </div>
        {f.document && <p className="text-xs text-emerald-700">Attached: {f.document.filename}</p>}
        <div className="flex gap-2">
          <button className={btnGhost} onClick={() => set("initiating_filing", null)} data-testid="intake-filing-skip-btn">Skip — no initiating filing</button>
        </div>
        <p className="text-xs text-slate-400">The filing enters the Clerk review queue as SUBMITTED; on acceptance it is FILED and docketed automatically (e.g. COMPLAINT_FILED).</p>
        <ExtraDocuments docs={form.documents || []} setDocs={(d) => set("documents", d)} filingTypes={cfg.filing_types || []} confidentialityLevels={cfg.confidentiality_levels} />
      </div>
    </Panel>
  );
}


function RoutingAssist({ form, set, setForm, cfg }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const evaluate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/ujms/routing/evaluate", { context: "INTAKE_PREVIEW", facts: { case_type: form.case_type, case_subtype: form.case_subtype || "", amount_in_controversy: form.amount_in_controversy === "" || form.amount_in_controversy == null ? null : Number(form.amount_in_controversy), municipality: form.municipality || null, charge_class: form.charge_class || null, is_srl: !!form.is_srl, jury_demand: !!form.jury_demand, fee_waiver: !!form.fee_waiver_requested, channel: "COUNTER", party_count: form.parties.length } });
      setRes(r.data);
      if (r.data.matched) {
        const div = cfg.divisions.find((d) => d.id === r.data.division_id);
        setForm((f) => ({ ...f, division_id: div ? div.id : f.division_id, district_id: r.data.district_id || f.district_id, _routing: r.data }));
        toast.success(`Routed to ${r.data.division_name}${r.data.district_name ? " / " + r.data.district_name : ""}`);
      } else toast.error("No routing rule matched — choose the division manually");
    } catch (e) { toast.error(e.response?.data?.detail || "Routing evaluation failed"); }
    setBusy(false);
  };
  return (
    <div className="border-t border-slate-200 p-4 space-y-3" data-testid="intake-routing-assist">
      <div className="flex items-center justify-between">
        <div><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Rules engine — jurisdictional routing, Illinois Supreme Court checks &amp; Rule 298</div><div className="text-xs text-slate-500">Give the engine the filing facts and it applies the court's routing rules and General Administrative Orders (Administration › Routing rules engine).</div></div>
        <button className={btnPrimary} disabled={busy || !form.case_type} onClick={evaluate} data-testid="intake-route-btn">{busy ? "Evaluating…" : "Suggest routing"}</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Amount in controversy ($)"><input type="number" className={inputCls} value={form.amount_in_controversy ?? ""} onChange={(e) => set("amount_in_controversy", e.target.value)} data-testid="intake-amount" /></Field>
        <Field label="Municipality"><input className={inputCls} value={form.municipality || ""} onChange={(e) => set("municipality", e.target.value)} placeholder="Chicago, Skokie, Maywood…" data-testid="intake-municipality" /></Field>
        {form.case_type === "CRIMINAL" && <Field label="Highest charge class"><input className={inputCls} value={form.charge_class || ""} onChange={(e) => set("charge_class", e.target.value.toUpperCase())} placeholder="X, 1–4, A, B, C" data-testid="intake-charge-class" /></Field>}
        <div className="flex flex-col gap-1 text-sm pt-4">
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!form.is_srl} onChange={(e) => set("is_srl", e.target.checked)} data-testid="intake-srl" /> Self-represented litigant</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!form.jury_demand} onChange={(e) => set("jury_demand", e.target.checked)} data-testid="intake-jury" /> Jury demand</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!form.auto_route} onChange={(e) => set("auto_route", e.target.checked)} data-testid="intake-auto-route" /> Let the rules engine route on create</label>
        </div>
      </div>
      <div className="border border-slate-200 rounded-sm p-3 space-y-2 bg-slate-50" data-testid="intake-fee-waiver">
        <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={!!form.fee_waiver_requested} onChange={(e) => set("fee_waiver_requested", e.target.checked)} data-testid="intake-fee-waiver-check" /> Application for Waiver of Court Fees (Ill. S. Ct. R. 298) — accept the filing and suspend fees pending the judge's ruling</label>
        {form.fee_waiver_requested && <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Basis"><select className={selectCls} value={form.fee_waiver_basis || "INCOME_AT_OR_BELOW_125_PCT"} onChange={(e) => set("fee_waiver_basis", e.target.value)} data-testid="intake-fee-waiver-basis"><option value="PUBLIC_BENEFITS">Receives means-tested public benefits</option><option value="INCOME_AT_OR_BELOW_125_PCT">Income at or below 125% of poverty level</option><option value="INCOME_125_TO_200_PCT_HARDSHIP">Income 125–200% with expenses exceeding income</option><option value="OTHER_HARDSHIP">Other substantial hardship</option></select></Field>
          <Field label="Statement"><input className={inputCls} value={form.fee_waiver_statement || ""} onChange={(e) => set("fee_waiver_statement", e.target.value)} data-testid="intake-fee-waiver-statement" /></Field>
        </div>}
      </div>
      {res && <SimResult res={res} />}
    </div>
  );
}
